import React, { useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import EntityGraph from "../../Components/Charts/EntityGraph";
import Sparkline from "../../Components/Charts/Sparkline";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  ChevronDownIcon,
  DownloadIcon,
  ExpandIcon,
  EyeIcon,
  InfoIcon,
  NetworkIcon,
  PlusIcon,
  SearchIcon,
  ShareIcon,
  UserCheckIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  CONNECTION_RISK,
  DEPTH_LEVELS,
  HIGH_RISK_CONNECTIONS,
  INTERMEDIARIES,
  MAP_CENTER,
  MAP_NODES,
  POTENTIAL_GROUPS,
  REL_STATS,
  RELATIONSHIP_TYPES,
  RISK_COLORS,
  RISK_LEVELS,
  TOTAL_RELATIONSHIPS,
  TYPE_BREAKDOWN,
} from "./data";
import "./Relationships.css";

const STAT_ICONS = {
  network: NetworkIcon,
  users: UsersIcon,
  group: UsersIcon,
  alert: AlertTriangleIcon,
  userCheck: UserCheckIcon,
};

const GRAPH_TOOLS = [
  { key: "zoom", label: "Zoom to fit", Icon: SearchIcon },
  { key: "paths", label: "Show paths", Icon: ShareIcon },
  { key: "expand", label: "Expand nodes", Icon: PlusIcon },
  { key: "full", label: "Full screen", Icon: ExpandIcon },
];

function CardHeader({ title, info, action }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">
        {title}
        {info && (
          <span className="dgi-card__info" title={`About ${title}`}>
            <InfoIcon size={15} />
          </span>
        )}
      </h2>
      {action}
    </div>
  );
}

export default function Relationships() {
  const [searchTab, setSearchTab] = useState("Taxpayer");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [risk, setRisk] = useState("");
  const [depth, setDepth] = useState("2 Levels");

  // The map is served for the requested depth; only nodes within it are drawn.
  const depthLevels = Number(depth.split(" ")[0]);
  const visibleNodes = MAP_NODES.filter((node) =>
    risk ? node.risk === risk : true
  );

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Relationship Analysis</h1>
          <p className="page__subtitle">
            Discover and analyze connections between taxpayers, entities, and
            other related parties.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <CalendarIcon size={17} />
            01 Jan 2026 - 01 Sep 2026
            <ChevronDownIcon size={16} />
          </button>
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <DownloadIcon size={17} />
            Export Report
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Relationship summary">
        {REL_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="rel-stat h-100">
                <div className="rel-stat__top">
                  <span className={`kpi__icon kpi__icon--${card.tone}`}>
                    <Icon size={22} />
                  </span>
                  <div className="rel-stat__meta">
                    <p className="kpi__label">{card.label}</p>
                    <p className="rel-stat__value">
                      {card.value}
                      <span className="kpi__delta">
                        <ArrowUpRightIcon size={13} />
                        {card.delta}
                      </span>
                    </p>
                    <p className="rel-stat__foot">vs last period</p>
                  </div>
                </div>
                <Sparkline values={card.spark} color={card.color} />
              </article>
            </div>
          );
        })}
      </section>

      {/* ------------------------------------------- explore / map / risk -- */}
      <div className="rel-main">
        {/* ------------------------------------------------- left column -- */}
        <div className="stack">
          <section className="dgi-card">
            <CardHeader title="Search &amp; Explore" />

            <div className="tabs">
              {["Taxpayer", "NIF / Reference"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={searchTab === tab}
                  className={`tabs__tab${
                    searchTab === tab ? " tabs__tab--active" : ""
                  }`}
                  onClick={() => setSearchTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="filters__search rel-search">
              <input
                type="search"
                placeholder="Search by name, NIF or reference..."
                aria-label={`Search by ${searchTab}`}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <SearchIcon size={17} />
            </div>

            <label className="rel-field" htmlFor="rel-type">
              <span className="rel-field__label">
                Relationship Type
                <span className="dgi-card__info" title="About relationship type">
                  <InfoIcon size={13} />
                </span>
              </span>
              <span className="select select--block">
                <select
                  id="rel-type"
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                >
                  {RELATIONSHIP_TYPES.map((option) => (
                    <option key={option.label} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            </label>

            <label className="rel-field" htmlFor="rel-risk">
              <span className="rel-field__label">Risk Level</span>
              <span className="select select--block">
                <select
                  id="rel-risk"
                  value={risk}
                  onChange={(event) => setRisk(event.target.value)}
                >
                  <option value="">All Risk Levels</option>
                  {RISK_LEVELS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            </label>

            <label className="rel-field" htmlFor="rel-depth">
              <span className="rel-field__label">
                Depth Level
                <span className="dgi-card__info" title="About depth level">
                  <InfoIcon size={13} />
                </span>
              </span>
              <span className="select select--block">
                <select
                  id="rel-depth"
                  value={depth}
                  onChange={(event) => setDepth(event.target.value)}
                >
                  {DEPTH_LEVELS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            </label>

            <button type="button" className="dgi-btn dgi-btn--primary rel-explore">
              Explore Relationship
            </button>
          </section>

          <section className="dgi-card">
            <CardHeader title="Relationship Types" />
            <DonutChart
              segments={TYPE_BREAKDOWN}
              total={TOTAL_RELATIONSHIPS.toLocaleString("en-US")}
              totalLabel="Total"
            />
            <button type="button" className="dgi-card__link">
              View Relationship Report <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>

        {/* -------------------------------------------------------- map -- */}
        <section className="dgi-card">
          <CardHeader
            title="Entity Relationship Map"
            info
            action={
              <div className="egraph__tools">
                {GRAPH_TOOLS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    className="egraph__tool"
                    aria-label={label}
                    title={label}
                  >
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            }
          />

          <ul className="egraph__legend">
            {Object.entries(RISK_COLORS).map(([label, color]) => (
              <li key={label}>
                <span className="donut__swatch" style={{ background: color }} />
                {label === "Unknown" ? "Unknown" : `${label} Risk`}
              </li>
            ))}
            <li className="egraph__legend-line">
              <span className="egraph__line" />
              Direct
            </li>
            <li className="egraph__legend-line">
              <span className="egraph__line egraph__line--dashed" />
              Indirect
            </li>
          </ul>

          <EntityGraph center={MAP_CENTER} nodes={visibleNodes} />

          <div className="egraph__foot">
            <p className="egraph__note">
              Showing relationships up to {depthLevels} level
              {depthLevels === 1 ? "" : "s"}
            </p>
            <button
              type="button"
              className="dgi-card__link dgi-card__link--inline"
            >
              View Full Screen <ArrowRightIcon size={16} />
            </button>
          </div>
        </section>

        {/* ------------------------------------------------ right column -- */}
        <div className="stack">
          <section className="dgi-card">
            <CardHeader title="Relationship Risk Distribution" />
            <DonutChart
              segments={CONNECTION_RISK}
              total={HIGH_RISK_CONNECTIONS.toLocaleString("en-US")}
              totalLabel="High Risk Connections"
            />
            <button type="button" className="dgi-card__link">
              View Risk Analysis <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <CardHeader title="Top Intermediaries" />
            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--compact">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Connections</th>
                    <th>Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {INTERMEDIARIES.map((item) => (
                    <tr key={item.name}>
                      <td>{item.name}</td>
                      <td>{item.connections}</td>
                      <td>
                        <span className={`chip chip--${item.risk.toLowerCase()}`}>
                          {item.risk}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="dgi-card__link">
              View All Intermediaries <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>

      {/* ---------------------------------------------- potential groups -- */}
      <section className="dgi-card">
        <CardHeader title="Potential Groups" info />

        <div className="dgi-table-wrap">
          <table className="dgi-table dgi-table--groups">
            <thead>
              <tr>
                <th>Group Name / ID</th>
                <th>Members</th>
                <th>Primary Activity</th>
                <th>Risk Level</th>
                <th>Confidence Score</th>
                <th>Indicators</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {POTENTIAL_GROUPS.map((group) => (
                <tr key={group.id}>
                  <td>
                    <button type="button" className="link">
                      {group.name}
                    </button>
                    <span className="dgi-table__sub">{group.id}</span>
                  </td>
                  <td>{group.members}</td>
                  <td>{group.activity}</td>
                  <td>
                    <span className={`chip chip--${group.risk.toLowerCase()}`}>
                      {group.risk}
                    </span>
                  </td>
                  <td>
                    <span className="score">
                      <span className="score__value">
                        <strong>{group.confidence}%</strong>
                      </span>
                      <span className="score__track">
                        <span
                          className="score__fill score__fill--good"
                          style={{ width: `${group.confidence}%` }}
                        />
                      </span>
                    </span>
                  </td>
                  <td className="rel-indicators">{group.indicators}</td>
                  <td>{group.updated}</td>
                  <td>
                    <button
                      type="button"
                      className="row-actions__btn"
                      aria-label={`View ${group.name}`}
                    >
                      <EyeIcon size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="dgi-card__link">
          View All Potential Groups <ArrowRightIcon size={16} />
        </button>
      </section>
    </div>
  );
}
